import { OzProductCard, PRODUCT_CARD_VARIANTS } from '@omaze/omaze-ui';
import classNames from 'classnames';
import React, { ReactElement } from 'react';
import 'lazysizes';

import 'lazysizes/plugins/attrchange/ls.attrchange';

import { IDataLayer, IUseGTMDataLayer, useGTMDataLayer } from '../useGTMDataLayer';

import { SweepstakesTag } from './SweepstakesTag/SweepstakesTag';

export interface ISweepstakesImage {
    src: string;
    alt: string;
}

export interface ISweepstakes {
    image: ISweepstakesImage;
    charity: string;
    title: string;
    url: string;
    handle: string;
    launchDate: Date;
    closeDate: Date;
    hasWinner: boolean;
}

export interface ISweepstakesCardProps {
    'data-testid'?: string;
    sweepstakes: ISweepstakes;
    className?: string;
}

function generateImageSrcset (src: string): string {
    const widths: number[] =  [
        480,
        650,
        812,
        1049,
        1249,
        1439,
        1600,
        2000,
        2400,
    ];

    const srcSet: string[] = [];

    widths.forEach((width: number): void => {
        srcSet.push(`${src} ${width}w`);
    });

    return srcSet
        .join(', ');
}

function createSweepstakeTracking (): IDataLayer {
    /* eslint-disable camelcase */
    return {
        event: 'click',
        ga_category: 'Ecommerce',
        ga_action: 'Click Product',
        ga_label: 'Click Product',
    };
    /* eslint-enable camelcase */
}

export function SweepstakesCard (props: ISweepstakesCardProps): ReactElement | null {
    const { sweepstakes, 'data-testid': dataTestid } : ISweepstakesCardProps = props;
    const { image, charity, title, url, handle, closeDate, hasWinner }: ISweepstakes = sweepstakes;
    const { src, alt }: ISweepstakesImage = image;
    const fallbackAltText: string = title;
    const fallbackUrl: string =`/products/${handle}`;
    const { pushEvent }: IUseGTMDataLayer = useGTMDataLayer(createSweepstakeTracking());

    // todo: h-full and w-full should not be needed on the image, inset-0 should handle the sizing of the image
    return (
        <a href={url || fallbackUrl} data-testid={dataTestid} onClick={pushEvent}>
            <OzProductCard variant={PRODUCT_CARD_VARIANTS.FLOATING}>
                <OzProductCard.Hero>
                    <img
                        className={classNames('lazyload inset-0 object-cover absolute h-full w-full', {
                            'bg-black-10': !src,
                        })}
                        alt={alt || fallbackAltText}
                        data-src={src}
                        data-sizes="auto"
                        data-srcset={generateImageSrcset(src)}
                    />
                </OzProductCard.Hero>
                <OzProductCard.Tags>
                    <SweepstakesTag hasWinner={hasWinner} closeDate={closeDate} />
                </OzProductCard.Tags>
                <OzProductCard.Body>
                    <OzProductCard.Title>
                        Support {charity}
                    </OzProductCard.Title>
                    <OzProductCard.Subtitle>
                        {title}
                    </OzProductCard.Subtitle>
                </OzProductCard.Body>
            </OzProductCard>
        </a>
    );
}
